import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import { BellIcon } from './IconSet';

/* ── Constants ──────────────────────────────────────────────────────────── */
const NOTIFICATION_TTL_MS = 24 * 60 * 60 * 1000; // 24 h
const SNOOZE_DURATION_MS = 4 * 60 * 60 * 1000; // 4 h
const POLL_INTERVAL_MS = 60_000; // 1 min
const RELATIVE_TIME_TICK = 30_000; // update "X min ago" every 30 s

const LS_DISMISSED = 'nn_dismissed_notifs';
const LS_SNOOZED = 'nn_snoozed_notifs';
const LS_DND = 'nn_dnd';
const EVT_MESSAGES_READ = 'messages:read';

function buildRealtimeWsUrl(token) {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  if (API_BASE) {
    try {
      const apiUrl = new URL(API_BASE, window.location.origin);
      return `${apiUrl.protocol === 'https:' ? 'wss:' : 'ws:'}//${
        apiUrl.host
      }/ws?token=${encodeURIComponent(token)}`;
    } catch {
      // Fallback to localhost dev URL below
    }
  }

  return `${protocol}//${window.location.hostname}:5000/ws?token=${encodeURIComponent(token)}`;
}

/* ── Relative time helper ───────────────────────────────────────────────── */
function timeAgo(ts) {
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) {
    return 'just now';
  }
  const min = Math.floor(sec / 60);
  if (min < 60) {
    return `${min} min ago`;
  }
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    return `${hr}h ago`;
  }
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

function getInitials(name) {
  return String(name || 'U')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

/* ── Critical-alert chime via Web Audio API ─────────────────────────────── */
let audioCtx = null;
function playAlertChime() {
  try {
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, audioCtx.currentTime);
    osc.frequency.setValueAtTime(1047, audioCtx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.35);
    osc.start(audioCtx.currentTime);
    osc.stop(audioCtx.currentTime + 0.35);
  } catch {
    // Audio not available
  }
}

/* ── Group labels ───────────────────────────────────────────────────────── */
const GROUP_LABELS = {
  messages: '💬 Messages',
  account: '🎉 Account',
  general: '📣 General',
};

/* ── Per-type icon for persisted (DB-backed) notifications ────────────────
   createNotification()/notifyUserAcrossChannels() call sites in the server
   (see server/notifications-api.js, server/index-db.js, server/blog-posts-api.js,
   server/jobs/notification-cron.js) each pass a `type` string - this maps
   that to a fitting icon so e.g. a flagged listing doesn't show the same
   🎉 as an approved referral. Falls back to 🎉 for any type not listed here,
   matching the previous blanket behavior. */
const NOTIF_TYPE_ICON = {
  content_action_required: '🚩',
  content_issue_reminder: '🚩',
  content_issue_escalated: '⚠️',
  content_issue_final_warning: '🚨',
  content_issue_removed: '🚫',
  guide_rejected: '📝',
  guide_changes_requested: '📝',
  contributor_rejected: '📝',
  agent_application_rejected: '📝',
  booking_requested: '📅',
  booking_confirmed: '📅',
  booking_declined: '📅',
  booking_cancelled: '📅',
  hold_requested: '📌',
  hold_approved: '📌',
  hold_declined: '📌',
  hold_released: '📌',
  hold_expired: '📌',
  price_drop: '💰',
  listing_saved: '❤️',
  new_listings_search: '🔎',
  viewing_recommendation: '🏠',
};

const DB_NOTIF_PREFIX = 'db-';

function groupNotifications(items) {
  const groups = {};
  for (const n of items) {
    const g = n.group || 'general';
    if (!groups[g]) {
      groups[g] = [];
    }
    groups[g].push(n);
  }
  return Object.entries(groups).map(([key, notifs]) => ({
    key,
    label: GROUP_LABELS[key] || key,
    items: notifs,
  }));
}

/* ── Component ──────────────────────────────────────────────────────────── */
export default function HeaderNotificationBell() {
  const { user, authedFetch } = useAuth();
  const navigate = useNavigate();
  const bellRef = useRef(null);
  const prevCountRef = useRef(0);
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const refreshRef = useRef(null);
  const toastTimerRef = useRef(null);

  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [incomingToast, setIncomingToast] = useState(null);
  const [, setTick] = useState(0);

  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(LS_DISMISSED) || '[]'));
    } catch {
      return new Set();
    }
  });
  const [snoozedMap, setSnoozedMap] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LS_SNOOZED) || '{}');
    } catch {
      return {};
    }
  });
  const [dnd, setDnd] = useState(() => {
    try {
      return localStorage.getItem(LS_DND) === '1';
    } catch {
      return false;
    }
  });
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  /* ── Persist ────────────────────────────────────────────────────────── */
  useEffect(() => {
    localStorage.setItem(LS_DISMISSED, JSON.stringify([...dismissedIds]));
  }, [dismissedIds]);
  useEffect(() => {
    localStorage.setItem(LS_SNOOZED, JSON.stringify(snoozedMap));
  }, [snoozedMap]);
  useEffect(() => {
    localStorage.setItem(LS_DND, dnd ? '1' : '0');
  }, [dnd]);

  /* ── Tab title badge ────────────────────────────────────────────────── */
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '');
    if (notifications.length > 0 && !dnd) {
      document.title = `(${notifications.length}) ${base}`;
    } else {
      document.title = base;
    }
    return () => {
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    };
  }, [notifications.length, dnd]);

  /* ── Relative-time tick ─────────────────────────────────────────────── */
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), RELATIVE_TIME_TICK);
    return () => clearInterval(id);
  }, []);

  /* ── Outside click ──────────────────────────────────────────────────── */
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClick = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  /* ── Un-snooze expired ──────────────────────────────────────────────── */
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      setSnoozedMap((prev) => {
        const next = {};
        let changed = false;
        for (const [nid, expiresAt] of Object.entries(prev)) {
          if (expiresAt > now) {
            next[nid] = expiresAt;
          } else {
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  /* ── Fetch + build ──────────────────────────────────────────────────── */
  const refresh = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      return;
    }
    const now = Date.now();
    const cutoff = now - NOTIFICATION_TTL_MS;
    const items = [];

    // ── Unread messages (all roles) ───────────────────────────────
    try {
      // authedFetch logs out automatically on a 401 (session died server-side
      // since this tab last checked - logged in elsewhere, session cleanup,
      // server restart) instead of retrying a dead session every minute forever.
      const msgRes = await authedFetch(`${API_BASE}/api/messages/unread-count`);
      if (msgRes.status === 401) {
        return;
      }
      if (msgRes.ok) {
        const msgData = await msgRes.json();
        const count = msgData.unreadCount || 0;
        if (count > 0) {
          items.push({
            id: `unread-messages-${count}`,
            icon: '💬',
            title: 'New Messages',
            body: `You have ${count} unread message${count !== 1 ? 's' : ''}.`,
            type: 'info',
            group: 'messages',
            link: '/messages',
            createdAt: now,
          });
        }
      }
    } catch {
      // Silently fail
    }

    // ── Persisted account notifications (e.g. role grants) ─────────
    try {
      const notifRes = await authedFetch(`${API_BASE}/notifications`);
      if (notifRes.status === 401) {
        return;
      }
      if (notifRes.ok) {
        const notifData = await notifRes.json();
        const rows = Array.isArray(notifData.notifications) ? notifData.notifications : [];
        rows
          .filter((n) => !n.read_at)
          .forEach((n) => {
            items.push({
              id: `${DB_NOTIF_PREFIX}${n.id}`,
              icon: NOTIF_TYPE_ICON[n.type] || '🎉',
              title: n.title,
              body: n.body || '',
              type: 'info',
              group: 'account',
              link: n.link || null,
              createdAt: new Date(n.created_at).getTime(),
            });
          });
      }
    } catch {
      // Silently fail
    }

    const visible = items.filter((n) => {
      if (n.createdAt < cutoff) {
        return false;
      }
      if (dismissedIds.has(n.id)) {
        return false;
      }
      const snoozeExpiry = snoozedMap[n.id];
      if (snoozeExpiry && snoozeExpiry > now) {
        return false;
      }
      return true;
    });

    const liveIds = new Set(items.map((n) => n.id));
    setDismissedIds((prev) => {
      const cleaned = new Set([...prev].filter((id) => liveIds.has(id)));
      return cleaned.size !== prev.size ? cleaned : prev;
    });

    setNotifications(visible);
  }, [user, authedFetch, dismissedIds, snoozedMap]);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    const handleMessagesRead = () => {
      refreshRef.current?.();
    };

    window.addEventListener(EVT_MESSAGES_READ, handleMessagesRead);
    return () => window.removeEventListener(EVT_MESSAGES_READ, handleMessagesRead);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const showIncomingMessageToast = useCallback(
    (payload = {}) => {
      if (dnd) {
        return;
      }

      const senderName = payload.senderName || 'Someone';
      const rawPreview =
        typeof payload.messagePreview === 'string' ? payload.messagePreview.trim() : '';
      const preview = rawPreview.length > 110 ? `${rawPreview.slice(0, 110)}…` : rawPreview;

      setIncomingToast({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: 'New Message',
        body: preview || 'You just received a new message.',
        senderName,
        senderAvatar: payload.senderAvatar || null,
        fromUserId: payload.senderId || null,
        conversationId: payload.conversationId || null,
      });

      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }

      toastTimerRef.current = setTimeout(() => {
        setIncomingToast(null);
      }, 6000);
    },
    [dnd],
  );

  /* ── WebSocket realtime refresh ─────────────────────────────────────── */
  useEffect(() => {
    if (!user?.id) {
      return undefined;
    }

    let cancelled = false;

    const connect = async () => {
      if (cancelled) {
        return;
      }

      let ticket;
      try {
        const res = await authedFetch(`${API_BASE}/auth/ws-ticket`);
        if (!res.ok) {throw new Error('ws-ticket request failed');}
        ({ ticket } = await res.json());
      } catch {
        reconnectTimerRef.current = setTimeout(connect, 3000);
        return;
      }
      if (cancelled || !ticket) {
        return;
      }

      try {
        const socket = new WebSocket(buildRealtimeWsUrl(ticket));
        socketRef.current = socket;

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data || '{}');
            if (data.type === 'message:new') {
              refreshRef.current?.();
              showIncomingMessageToast(data.payload);
            }
          } catch {
            // Ignore malformed socket payloads
          }
        };

        socket.onclose = () => {
          if (cancelled) {
            return;
          }
          reconnectTimerRef.current = setTimeout(connect, 3000);
        };

        socket.onerror = () => {
          try {
            socket.close();
          } catch {
            // Ignore close errors
          }
        };
      } catch {
        reconnectTimerRef.current = setTimeout(connect, 3000);
      }
    };

    connect();

    return () => {
      cancelled = true;

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      const current = socketRef.current;
      if (current) {
        try {
          current.close();
        } catch {
          // Ignore close errors
        }
        socketRef.current = null;
      }
    };
  }, [user?.id, showIncomingMessageToast, authedFetch]);

  /* ── Polling ────────────────────────────────────────────────────────── */
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  /* ── Sound for new critical alerts ──────────────────────────────────── */
  useEffect(() => {
    if (dnd) {
      return;
    }
    const hasCritical = notifications.some((n) => n.type === 'critical');
    if (hasCritical && notifications.length > prevCountRef.current) {
      playAlertChime();
    }
    prevCountRef.current = notifications.length;
  }, [notifications, dnd]);

  /* ── Prune expired ──────────────────────────────────────────────────── */
  useEffect(() => {
    const id = setInterval(() => {
      const cutoff = Date.now() - NOTIFICATION_TTL_MS;
      setNotifications((prev) => prev.filter((n) => n.createdAt > cutoff));
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  if (!user) {
    return null;
  }

  const activeCount = dnd ? 0 : notifications.length;
  const grouped = groupNotifications(notifications);

  const dismissAll = () => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      notifications.forEach((n) => next.add(n.id));
      return next;
    });
    notifications.forEach((n) => markServerNotificationRead(n.id));
    setNotifications([]);
    setOpen(false);
  };

  const markServerNotificationRead = (id) => {
    if (!id.startsWith(DB_NOTIF_PREFIX)) {
      return;
    }
    const rawId = id.slice(DB_NOTIF_PREFIX.length);
    authedFetch(`${API_BASE}/notifications/${rawId}/read`, { method: 'PATCH' }).catch(() => {
      // Silently fail - it'll just resurface on next poll, no harm done
    });
  };

  const dismissOne = (id) => {
    setDismissedIds((prev) => new Set(prev).add(id));
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    markServerNotificationRead(id);
  };

  const snoozeOne = (id) => {
    setSnoozedMap((prev) => ({ ...prev, [id]: Date.now() + SNOOZE_DURATION_MS }));
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleNotifClick = (n) => {
    markServerNotificationRead(n.id);
    setNotifications((prev) => prev.filter((item) => item.id !== n.id));
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  };

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const typeClass = (type) =>
    type === 'critical'
      ? 'notif-item--critical'
      : type === 'warning'
        ? 'notif-item--warning'
        : 'notif-item--info';

  const closeToast = () => {
    setIncomingToast(null);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  };

  const openMessagesFromToast = () => {
    const conversationId = incomingToast?.conversationId;
    closeToast();
    if (conversationId) {
      navigate(`/messages?conversationId=${encodeURIComponent(conversationId)}`);
      return;
    }
    navigate('/messages');
  };

  return (
    <div className="header-notif" ref={bellRef}>
      {incomingToast && (
        <div className="header-notif__toast" role="status" aria-live="polite">
          <button
            type="button"
            className="header-notif__toast-main"
            onClick={openMessagesFromToast}
            title="Open messages"
          >
            {incomingToast.senderAvatar ? (
              <img
                className="header-notif__toast-avatar"
                src={incomingToast.senderAvatar}
                alt={incomingToast.senderName}
              />
            ) : (
              <span className="header-notif__toast-avatar header-notif__toast-avatar--fallback">
                {getInitials(incomingToast.senderName)}
              </span>
            )}
            <span className="header-notif__toast-text">
              <strong>{incomingToast.title}</strong>
              <span>From {incomingToast.senderName}</span>
              <span>{incomingToast.body}</span>
            </span>
          </button>
          <button
            type="button"
            className="header-notif__toast-close"
            onClick={closeToast}
            aria-label="Dismiss message toast"
          >
            ✕
          </button>
        </div>
      )}

      <button
        type="button"
        className={`header-notif__bell${activeCount ? ' header-notif__bell--active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${activeCount ? ` (${activeCount} new)` : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <BellIcon size={18} />
        {activeCount > 0 && <span className="header-notif__count">{activeCount}</span>}
      </button>

      {open && (
        <div className="header-notif__dropdown header-notif__dropdown--open" role="menu">
          <div className="header-notif__top">
            <span className="header-notif__title">Notifications</span>
            <div className="header-notif__actions">
              <button
                type="button"
                className={`header-notif__dnd${dnd ? ' header-notif__dnd--on' : ''}`}
                onClick={() => setDnd((d) => !d)}
                title={dnd ? 'Turn off Do Not Disturb' : 'Do Not Disturb'}
                aria-pressed={dnd}
              >
                {dnd ? '🔕' : '🔔'}
              </button>
              {notifications.length > 0 && (
                <button type="button" className="header-notif__read-all" onClick={dismissAll}>
                  Dismiss all
                </button>
              )}
            </div>
          </div>

          {dnd && (
            <p className="header-notif__dnd-banner">🔕 Do Not Disturb — notifications silenced.</p>
          )}

          {notifications.length === 0 ? (
            <p className="header-notif__empty">
              {dnd ? 'Notifications silenced.' : 'No notifications right now.'}
            </p>
          ) : (
            <div className="header-notif__list">
              {grouped.map((g) => (
                <div key={g.key} className="notif-group">
                  <button
                    type="button"
                    className="notif-group__header"
                    onClick={() => toggleGroup(g.key)}
                    aria-expanded={!collapsedGroups.has(g.key)}
                  >
                    <span className="notif-group__label">{g.label}</span>
                    <span className="notif-group__count">{g.items.length}</span>
                    <span
                      className={`notif-group__chevron${
                        collapsedGroups.has(g.key) ? ' notif-group__chevron--collapsed' : ''
                      }`}
                    >
                      ▾
                    </span>
                  </button>

                  {!collapsedGroups.has(g.key) && (
                    <ul className="notif-group__items">
                      {g.items.map((n) => (
                        <li
                          key={n.id}
                          className={`notif-item notif-item--enter ${typeClass(n.type)}`}
                          role="menuitem"
                        >
                          <button
                            type="button"
                            className="notif-item__link"
                            onClick={() => handleNotifClick(n)}
                            title={n.link ? 'Go to details' : undefined}
                          >
                            <span className="notif-item__icon">{n.icon}</span>
                            <div className="notif-item__body">
                              <strong className="notif-item__title">{n.title}</strong>
                              <span className="notif-item__text">{n.body}</span>
                              <span className="notif-item__time">{timeAgo(n.createdAt)}</span>
                            </div>
                          </button>
                          <div className="notif-item__controls">
                            <button
                              type="button"
                              className="notif-item__btn"
                              onClick={() => snoozeOne(n.id)}
                              aria-label="Snooze 4 hours"
                              title="Snooze 4 h"
                            >
                              💤
                            </button>
                            <button
                              type="button"
                              className="notif-item__btn"
                              onClick={() => dismissOne(n.id)}
                              aria-label="Dismiss"
                              title="Dismiss"
                            >
                              ✕
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
